import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { profileAPI } from '../lib/api';
import { 
    User, 
    Briefcase, 
    Building2, 
    MapPin, 
    ArrowLeft,
    Lock,
    LogIn,
    Shield
} from 'lucide-react';
import { Badge } from '../components/ui/badge';

const MemberProfilePage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading, login } = useAuth();
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            setLoading(false);
            return;
        }

        if (isAuthenticated && userId) {
            fetchMember();
        }
    }, [isAuthenticated, authLoading, userId]);

    const fetchMember = async () => {
        try {
            const response = await profileAPI.getUserProfile(userId);
            setMember(response.data);
        } catch (error) {
            console.error('Error fetching member:', error);
        } finally {
            setLoading(false);
        }
    };

    // Show login required screen if not authenticated
    if (!authLoading && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-background" data-testid="member-profile-login-required">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <Card className="text-center">
                        <CardContent className="pt-12 pb-10">
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Lock className="h-10 w-10 text-primary" />
                            </div>
                            <h1 className="font-heading text-3xl font-bold mb-4">
                                Members Only
                            </h1>
                            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                                Member profiles are only visible to HAAPE members. 
                                Please log in to view this profile.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button onClick={login} size="lg" className="gap-2" data-testid="login-btn">
                                    <LogIn className="h-4 w-4" />
                                    Member Login
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="max-w-2xl mx-auto px-4 py-12">
                    <Skeleton className="h-8 w-32 mb-8" />
                    <Card>
                        <CardContent className="p-8">
                            <div className="flex flex-col items-center text-center">
                                <Skeleton className="h-32 w-32 rounded-full mb-6" />
                                <Skeleton className="h-8 w-48 mb-2" />
                                <Skeleton className="h-5 w-32 mb-4" />
                                <Skeleton className="h-4 w-40" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    if (!member) {
        return (
            <div className="min-h-screen bg-background">
                <div className="max-w-2xl mx-auto px-4 py-12">
                    <Card>
                        <CardContent className="py-12 text-center">
                            <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h2 className="font-heading text-xl font-semibold mb-2">Member Not Found</h2>
                            <p className="text-muted-foreground mb-6">
                                This member profile doesn't exist or has been removed.
                            </p>
                            <Link to="/members">
                                <Button variant="outline">Back to Members</Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="member-profile-page">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Link to="/members">
                    <Button variant="ghost" className="gap-2 mb-6">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Members
                    </Button>
                </Link>

                <Card>
                    <CardContent className="p-8">
                        <div className="flex flex-col items-center text-center">
                            <Avatar className="h-32 w-32 mb-6">
                                <AvatarImage src={member.picture} alt={member.name} />
                                <AvatarFallback className="text-4xl">
                                    {member.name?.charAt(0) || 'M'}
                                </AvatarFallback>
                            </Avatar>

                            <h1 className="font-heading text-2xl font-bold mb-2">
                                {member.name}
                            </h1>

                            {member.role === 'chapter_director' && (
                                <Badge className="mb-3 bg-amber-500 hover:bg-amber-600 text-white" data-testid="cd-badge-profile">
                                    <Shield className="h-3.5 w-3.5 mr-1" />
                                    Chapter Director{member.chapter_region ? ` — ${member.chapter_region}` : ''}
                                </Badge>
                            )}

                            {member.title && (
                                <p className="text-lg text-muted-foreground flex items-center gap-2 mb-2">
                                    <Briefcase className="h-5 w-5" />
                                    {member.title}
                                </p>
                            )}

                            {member.organization && (
                                <p className="text-muted-foreground flex items-center gap-2 mb-2">
                                    <Building2 className="h-4 w-4" />
                                    {member.organization}
                                </p>
                            )}

                            {(member.city || member.state) && (
                                <p className="text-muted-foreground flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    {[member.city, member.state].filter(Boolean).join(', ')}
                                </p>
                            )}

                            {member.bio && (
                                <div className="mt-6 pt-6 border-t border-border w-full">
                                    <h3 className="font-semibold mb-2 text-left">About</h3>
                                    <p className="text-muted-foreground text-left whitespace-pre-wrap">
                                        {member.bio}
                                    </p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default MemberProfilePage;
